import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Wand2 } from "lucide-react";
import FormField from "../FormField/FormField";
import RichTextField from "../RichText/RichTextField";
import PagePicker from "./PagePicker";
import { draftTemplate, isBlankHtml } from "./storefrontPages";

const LABEL = { en: "Opening text (English)", ar: "Opening text (Arabic)" };
const PLACEHOLDER = {
  en: "e.g. Every fragrance we stock is 100% original…",
  ar: "مثال: جميع العطور لدينا أصلية 100٪…",
};

/**
 * One language's opening text: the rich text box, a way to start from a
 * draft written from the catalogue while the box is empty, and the link
 * button's page picker. The "Page" tab of the live preview beside the form
 * shows the result and the checklist of what Google wants.
 *
 * `heading` is the page's name as a shopper reads it (the typed page
 * heading, else the row's name) and seeds the draft's first line; `kind`
 * (category, subcategory, brand) shapes the draft; `currentPath` is the
 * page's own address, so the draft can read its facts and link to the pages
 * around it, and the picker leaves it out.
 */
export default function LandingCopyEditor({ name, lang, kind, value, onChange, readOnly, heading, currentPath }) {
  const { t } = useTranslation();
  const [insert, setInsert] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const rtl = lang === "ar";
  const empty = isBlankHtml(value);

  const startFromTemplate = async () => {
    setDrafting(true);
    onChange(await draftTemplate({ lang, kind, name: heading, currentPath }));
    setDrafting(false);
  };

  return (
    <div className="lp-copy">
      <FormField
        name={name}
        label={t(LABEL[lang])}
        hint={
          lang === "en"
            ? t(
                "Shown beside the products. Press the wand to start from a draft written from the catalogue, or write your own: a heading that says what the page sells, a short paragraph, then bullets linking to the pages under this one. The link button lists our pages. The Page tab on the right shows the result.",
              )
            : undefined
        }
        labelRight={
          !readOnly && empty ? (
            <button type="button" className="lp-template-btn" onClick={startFromTemplate} disabled={drafting}>
              <Wand2 size={12} aria-hidden="true" />
              {drafting ? t("Preparing…") : t("Start from a template")}
            </button>
          ) : undefined
        }
      >
        <RichTextField
          id={name}
          value={value}
          onChange={onChange}
          rtl={rtl}
          minHeight={180}
          headings={[2, 3]}
          placeholder={PLACEHOLDER[lang]}
          readOnly={readOnly}
          onLink={(inserter) => setInsert(() => inserter)}
        />
      </FormField>

      {insert && (
        <PagePicker
          lang={lang}
          currentPath={currentPath}
          onClose={() => setInsert(null)}
          onPick={(link) => {
            insert(link);
            setInsert(null);
          }}
        />
      )}
    </div>
  );
}
